"use client";

import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Crop, XCircle, ShieldCheck, X } from "lucide-react";
import { NatureLoader } from "@/components/shared/NatureLoader";
import { PhotoCropper } from "./PhotoCropper";

/**
 * Metadata captured at upload time (PLA2-372 / Sys10).
 * - `source`: where the image came from. Samples are pre-cleared.
 * - `rightsConfirmed`: user affirmed they have the right to use the photo.
 *   Auto-satisfied for samples.
 */
export interface UploadMeta {
  source: "upload" | "sample";
  rightsConfirmed: boolean;
}

interface PhotoUploaderProps {
  onUpload: (file: File, meta?: UploadMeta) => void;
  onUploadUrl?: (url: string) => void;
  isUploading?: boolean;
  error?: string | null;
  /** PLA2-945: backend unavailable — block every way of starting an upload. */
  disabled?: boolean;
}

// Photo coaching (PLA2-822). Most weak designs trace back to the photo: shot
// from across the street, the yard is a thin band under a lot of sky, so every
// outline the user draws leaves the model almost no room to plant.
const PHOTO_DOS = [
  {
    title: "Turn your phone sideways",
    body: "Wide photos give the designer more of your yard.",
  },
  {
    title: "Fill the frame with your planting area",
    body: "Beds and lawn should take up at least half the photo. A little sky is fine.",
  },
  {
    title: "Shoot in daylight",
    body: "Even light shows the designer what is already growing.",
  },
  {
    title: "One photo per area",
    body: "Planning several areas? Take one photo each, like the foundation bed or the driveway edge.",
  },
];

const PHOTO_DONTS = [
  {
    title: "Don't shoot from across the street",
    body: "Step closer so the yard isn't a thin strip at the bottom.",
  },
  {
    title: "Don't fill the frame with sky and trees",
    body: "Tilt the phone down until the ground fills most of the photo.",
  },
  {
    title: "Don't leave clutter in the frame",
    body: "No people, cars or trash cans.",
  },
  {
    title: "Don't squeeze several areas into one photo",
    body: "Each outline needs room to plant.",
  },
];

const SAMPLE_IMAGES = [
  "/samples/IMG_9830.jpg",
  "/samples/IMG_9833.jpg",
  "/samples/IMG_9834.jpg",
  "/samples/IMG_9836.jpg",
];

export function PhotoUploader({
  onUpload,
  onUploadUrl,
  isUploading = false,
  error = null,
  disabled = false,
}: PhotoUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [loadingSample, setLoadingSample] = useState<string | null>(null);
  // Photo-rights attestation (PLA2-372 / Sys10). Instead of cluttering the
  // upload screen with a checkbox, we hold the just-selected file here and ask
  // the user to confirm their rights in a popup before the upload fires. Samples
  // are pre-cleared and bypass this gate entirely.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  // "Crop to my yard" (PLA2-822): offered from the rights popup, so the upload
  // that fires is already the cropped photo.
  const [isCropping, setIsCropping] = useState(false);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file.type.startsWith("image/")) {
          requestUpload(file);
        }
      }
    },
    [disabled]
  );

  const handleFile = (file: File, meta: UploadMeta) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
    onUpload(file, meta);
  };

  // User-upload path: stash the selected file and open the rights popup. The
  // actual upload only fires once the user confirms in the modal.
  const requestUpload = (file: File) => {
    if (disabled) return;
    setPendingPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setPendingFile(file);
  };

  const cancelPending = useCallback(() => {
    setPendingFile(null);
    setPendingPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const confirmPendingUpload = (file: File | null = pendingFile) => {
    if (!file) return;
    handleFile(file, { source: "upload", rightsConfirmed: true });
    setIsCropping(false);
    setPendingFile(null);
    setPendingPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      requestUpload(files[0]);
    }
    e.target.value = ""; // allow re-selecting the same file after cancel
  };

  // Close the rights popup on Escape (the cropper handles its own Escape).
  useEffect(() => {
    if (!pendingFile || isCropping) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelPending();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingFile, isCropping, cancelPending]);

  // Revoke any outstanding object URL on unmount.
  useEffect(() => {
    return () => {
      setPendingPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  const handleSampleSelect = async (sampleUrl: string) => {
    if (disabled) return;
    setLoadingSample(sampleUrl);
    try {
      const response = await fetch(sampleUrl);
      const blob = await response.blob();
      const filename = sampleUrl.split("/").pop() || "sample.jpg";
      const file = new File([blob], filename, { type: blob.type });
      // Samples are pre-cleared: attestation is auto-satisfied, source = sample.
      handleFile(file, { source: "sample", rightsConfirmed: true });
    } catch (err) {
      console.error("Failed to load sample image:", err);
    } finally {
      setLoadingSample(null);
    }
  };

  // Determine what to show: Loader, Error, or Upload Area/Preview
  const showLoader = isUploading;
  const showError = !!error;
  const showPreview = preview && !isUploading && !error;

  return (
    <div className="w-full">
      {!showPreview ? (
        <div className="space-y-6">
          {/* Header Section */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-plantista-400 font-display">
              AI Plantscape Design Inspiration
            </h1>
            <p className="text-sm md:text-base text-plantista-300">
              Reimagine your landscape with zone-specific, personalized designs
            </p>
          </div>

          {/* Interaction Area */}
          <label
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`
              block border-2 border-dashed rounded-xl p-8 md:p-16 transition-all duration-300
              ${
                disabled && !showLoader
                  ? "pointer-events-none opacity-60 border-plantista-200 dark:border-plantista-800 bg-white dark:bg-plantista-900"
                  : showLoader
                    ? "pointer-events-none border-plantista-400 bg-plantista-50/50 dark:bg-plantista-900/50"
                    : showError
                      ? "border-red-500/50 bg-red-50 dark:bg-red-900/20"
                      : isDragging
                        ? "border-plantista-400 bg-plantista-50 dark:bg-plantista-900/80 scale-[1.02] shadow-sm cursor-pointer"
                        : "border-plantista-200 dark:border-plantista-800 bg-white dark:bg-plantista-900 hover:border-plantista-400 dark:hover:border-plantista-600 hover:bg-plantista-50 dark:hover:bg-plantista-900/80 cursor-pointer"
              }
            `}
          >
            <div className="flex flex-col items-center justify-center space-y-6">
              {showLoader ? (
                <>
                  {/* Loading Animation */}
                  <div className="p-4 bg-plantista-100 rounded-full shadow-sm border border-plantista-200">
                    <NatureLoader set="A" imageSize={60} />
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-plantista-700 dark:text-plantista-300">
                      Uploading your yard...
                    </p>
                    <p className="text-plantista-600 dark:text-plantista-400 mt-2 font-medium">
                      Our AI is preparing your space for transformation
                    </p>
                  </div>
                </>
              ) : showError ? (
                <>
                  {/* Error State */}
                  <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-full shadow-sm text-red-500 dark:text-red-400 border border-red-200 dark:border-red-800">
                    <svg
                      className="w-16 h-16"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="text-center max-w-md">
                    <p className="text-xl font-bold text-red-600 dark:text-red-400">
                      Upload failed
                    </p>
                    <p className="text-red-500 dark:text-red-300 mt-2 font-medium">
                      {error}
                    </p>
                    <div className="mt-6">
                      <span className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-100 dark:bg-red-900 dark:hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                        Try Again
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Default Upload Icon */}
                  <div
                    className={`p-6 bg-plantista-50 dark:bg-plantista-950 border border-plantista-200 dark:border-plantista-800 rounded-full shadow-sm transition-transform duration-300 ${isDragging ? "scale-110" : ""}`}
                  >
                    <img
                      src="/yard.svg"
                      alt="Yard icon"
                      className="w-16 h-16 md:w-20 md:h-20 opacity-80"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-plantista-900 dark:text-plantista-50">
                      {isDragging
                        ? "Ready to start!"
                        : "Drop your yard photo here"}
                    </p>
                    <p className="text-plantista-600 dark:text-plantista-300 mt-2 font-medium">
                      Or click to browse from your computer
                    </p>
                    <div className="mt-6 flex flex-col items-center gap-2">
                      <div className="flex items-center gap-4 text-xs text-plantista-500 dark:text-plantista-400 font-medium">
                        <span className="bg-plantista-100 dark:bg-plantista-950 border border-plantista-200 dark:border-plantista-800 px-2 py-1 rounded text-plantista-700 dark:text-plantista-300">
                          JPEG
                        </span>
                        <span className="bg-plantista-100 dark:bg-plantista-950 border border-plantista-200 dark:border-plantista-800 px-2 py-1 rounded text-plantista-700 dark:text-plantista-300">
                          PNG
                        </span>
                        <span className="bg-plantista-100 dark:bg-plantista-950 border border-plantista-200 dark:border-plantista-800 px-2 py-1 rounded text-plantista-700 dark:text-plantista-300">
                          WEBP
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <input
              type="file"
              className="sr-only"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileInput}
              disabled={showLoader || disabled}
              aria-label="Upload a photo of your yard"
            />
          </label>

          {/* Photo coaching (PLA2-822) */}
          {!showLoader && !showError && (
            <div className="rounded-xl border border-plantista-200 dark:border-plantista-800 bg-plantista-50/60 dark:bg-plantista-950/60 p-4">
              <p className="text-sm font-bold text-plantista-900 dark:text-plantista-50">
                Get the best design: frame your planting area
              </p>
              {/* Same kind of yard, shot two ways: the left framing leaves outlines almost no room to plant. */}
              <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <figure className="max-w-[160px]">
                    <div className="relative overflow-hidden rounded-lg border-2 border-plantista-500">
                      <img
                        src="/samples/IMG_9833.jpg"
                        alt="Photo where the lawn fills most of the frame"
                        loading="lazy"
                        className="block aspect-[3/4] w-full object-cover"
                      />
                      <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-plantista-600 px-2 py-0.5 text-[11px] font-bold text-white">
                        <CheckCircle2 className="h-3 w-3" /> Do
                      </span>
                    </div>
                    <figcaption className="mt-1 text-[11px] leading-tight text-plantista-600 dark:text-plantista-400">
                      The yard fills most of the frame.
                    </figcaption>
                  </figure>
                  <ul className="mt-3 space-y-2.5">
                    {PHOTO_DOS.map(({ title, body }) => (
                      <li key={title} className="flex items-start gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-plantista-600 dark:text-plantista-300" />
                        <div>
                          <p className="text-sm font-semibold text-plantista-900 dark:text-plantista-100">
                            {title}
                          </p>
                          <p className="text-xs text-plantista-600 dark:text-plantista-400">
                            {body}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <figure className="max-w-[160px]">
                    <div className="relative overflow-hidden rounded-lg border-2 border-red-400 dark:border-red-500/70">
                      <img
                        src="/tips/photo-dont.jpg"
                        alt="Photo where sky and tall trees fill most of the frame and the ground is a thin strip"
                        loading="lazy"
                        className="block aspect-[3/4] w-full object-cover object-top"
                      />
                      <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-bold text-white">
                        <XCircle className="h-3 w-3" /> Don&apos;t
                      </span>
                    </div>
                    <figcaption className="mt-1 text-[11px] leading-tight text-plantista-600 dark:text-plantista-400">
                      Mostly sky and trees. The yard is a thin strip.
                    </figcaption>
                  </figure>
                  <ul className="mt-3 space-y-2.5">
                    {PHOTO_DONTS.map(({ title, body }) => (
                      <li key={title} className="flex items-start gap-2">
                        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500 dark:text-red-400" />
                        <div>
                          <p className="text-sm font-semibold text-plantista-900 dark:text-plantista-100">
                            {title}
                          </p>
                          <p className="text-xs text-plantista-600 dark:text-plantista-400">
                            {body}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Sample images */}
          {!showLoader && !showError && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-plantista-200 dark:bg-plantista-800" />
                <span className="text-sm text-plantista-500 dark:text-plantista-400 font-medium">
                  or try a sample
                </span>
                <div className="flex-1 h-px bg-plantista-200 dark:bg-plantista-800" />
              </div>
              <div className="flex gap-3 justify-center flex-wrap">
                {SAMPLE_IMAGES.map((src) => (
                  <button
                    key={src}
                    onClick={() => handleSampleSelect(src)}
                    disabled={!!loadingSample || disabled}
                    aria-label="Use this sample yard photo"
                    className={`
                      relative w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-2 transition-all duration-200
                      ${
                        loadingSample === src
                          ? "border-plantista-400 opacity-70"
                          : "border-plantista-200 dark:border-plantista-800 hover:border-plantista-400 hover:scale-105"
                      }
                    `}
                  >
                    <img
                      src={src}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    {loadingSample === src && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="relative group overflow-hidden rounded-2xl shadow-2xl border-4 border-plantista-800">
          <img
            src={preview || ""}
            alt="Uploaded preview"
            className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <button
              onClick={() => {
                setPreview(null);
              }}
              className="px-6 py-3 bg-plantista-950 text-plantista-400 font-bold rounded-full shadow-xl hover:bg-plantista-900 border border-plantista-700 transform hover:-translate-y-1 transition-all"
            >
              Change Yard Image
            </button>
          </div>
        </div>
      )}

      {/* Photo-rights confirmation popup (PLA2-372 / Sys10). Shown after the
          user picks/drops a photo; the upload only fires once they confirm. */}
      {pendingFile && isCropping && (
        <PhotoCropper
          file={pendingFile}
          onCancel={() => setIsCropping(false)}
          onCropped={(cropped) => confirmPendingUpload(cropped)}
        />
      )}

      {pendingFile && !isCropping && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) cancelPending();
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="rights-modal-title"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Card */}
          <div className="relative w-full max-w-sm rounded-2xl border-2 border-plantista-950 dark:border-plantista-50 bg-white dark:bg-plantista-900 p-6 shadow-[6px_6px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 animate-in fade-in zoom-in duration-200">
            <button
              onClick={cancelPending}
              aria-label="Close"
              className="absolute right-3 top-3 rounded-full p-1 text-plantista-500 transition-colors hover:bg-plantista-100 dark:text-plantista-400 dark:hover:bg-plantista-800"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex flex-col items-center text-center">
              {/* Photo thumbnail */}
              {pendingPreview && (
                <div className="mb-4 w-24 h-24 overflow-hidden rounded-xl border-2 border-plantista-950 dark:border-plantista-50">
                  <img
                    src={pendingPreview}
                    alt="Your selected yard photo"
                    className="h-full w-full object-cover"
                  />
                </div>
              )}

              <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-plantista-100 dark:bg-plantista-800">
                <ShieldCheck className="h-5 w-5 text-plantista-600 dark:text-plantista-300" />
              </div>
              <h3
                id="rights-modal-title"
                className="font-display text-lg font-bold text-plantista-950 dark:text-white"
              >
                One quick check
              </h3>
              <p className="mt-1.5 text-sm text-plantista-600 dark:text-plantista-300">
                Please confirm you have the right to use this photo. It&apos;ll
                be used to generate your design.
              </p>

              <p className="mt-3 text-xs text-plantista-500 dark:text-plantista-400">
                Lots of sky, roof or street in the shot? Crop it so your yard
                fills the frame. Your design gets more room to work with.
              </p>

              <div className="mt-5 flex w-full flex-col gap-2.5">
                <button
                  onClick={() => confirmPendingUpload()}
                  className="w-full rounded-lg border-2 border-plantista-950 bg-plantista-400 px-6 py-3 font-bold text-plantista-950 shadow-[4px_4px_0px_0px] shadow-plantista-950 transition-all hover:bg-plantista-500 active:translate-x-[2px] active:translate-y-[2px] active:shadow-[2px_2px_0px_0px] dark:shadow-plantista-50"
                >
                  I have the right — continue
                </button>
                <button
                  onClick={() => setIsCropping(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-plantista-950 dark:border-plantista-50 bg-white dark:bg-plantista-900 px-6 py-2.5 font-bold text-plantista-950 dark:text-plantista-50 transition-colors hover:bg-plantista-50 dark:hover:bg-plantista-800"
                >
                  <Crop className="h-4 w-4" />I have the right — crop to my yard
                </button>
                <button
                  onClick={cancelPending}
                  className="w-full rounded-lg px-6 py-2 text-sm font-semibold text-plantista-600 transition-colors hover:bg-plantista-100 dark:text-plantista-300 dark:hover:bg-plantista-800"
                >
                  Choose a different photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
